import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { authFetch } from '../../auth';
import useSessionPresenceWebSocket from '../../utils/useSessionPresenceWebSocket';
import './LinkTimeChat.css';

function fmtTimeTz(ts, tz) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', timeZone: tz || 'UTC'
    });
  } catch (_) {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}

function abbrTz(tz) {
  if (!tz) return '';
  try {
    return Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(p => p.type === 'timeZoneName')?.value || '';
  } catch (_) {}
  return tz.split('/').pop().replace('_', ' ');
}

// Plain, unambiguous labels for presence vs call events.
// "entered"/"left" = in the room (waiting); "joined_call"/"left_call" = live A/V.
// Legacy "join"/"leave" still in the 7-day message history map to the same words.
function systemLabel(kind) {
  switch (kind) {
    case 'entered':
    case 'join': // legacy
      return 'is here';
    case 'joined_call': return 'joined the call';
    case 'left_call': return 'left the call';
    case 'left':
    case 'leave': // legacy
    default:
      return 'left';
  }
}

export default function LinkTimeChat({
  roomAlias,
  meetingCode,
  selfName,
  selfRole,
  selfTz,
  layout: layoutProp
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  // Responsive layout: media-query driven, prop overrides
  const layout = useMemo(() => {
    if (layoutProp) return layoutProp;
    var w = typeof window !== 'undefined' ? window.innerWidth : 1024;
    if (w < 640) return 'sheet';
    if (w < 1024) return 'drawer';
    return 'rail';
  }, [layoutProp]);

  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifPerm, setNotifPerm] = useState(
    (typeof Notification !== 'undefined') ? Notification.permission : 'unsupported'
  );
  const messagesEndRef = useRef(null);
  var roomKey = 'linktime_room:' + roomAlias + ':' + meetingCode;
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  // Drag-to-expand for drawer/sheet
  const dragStartY = useRef(0);
  const handleDragStart = useCallback((e) => {
    if (layout === 'rail') return;
    dragStartY.current = e.touches ? e.touches[0].clientY : e.clientY;
  }, [layout]);
  const handleDragEnd = useCallback((e) => {
    if (layout === 'rail') return;
    var endY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    var dy = endY - dragStartY.current;
    if (Math.abs(dy) < 30) return;
    setCollapsed(dy > 0);
  }, [layout]);

  // Fetch initial messages
  useEffect(() => {
    if (!roomAlias || !meetingCode) return;
    setLoading(true);
    authFetch(`/api/linktime/room/${roomAlias}/${meetingCode}/messages?limit=50`, { skipAuthRedirect: true })
      .then(r => r.json())
      .then(data => {
        if (data.messages && data.messages.length) setMessages(data.messages);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [roomAlias, meetingCode]);

  // Polling fallback for real-time messages (works even when WS subscribe fails)
  useEffect(() => {
    if (!roomAlias || !meetingCode) return;
    var active = true;
    var pollMs = 3000;
    var pollTimer;

    var doPoll = function() {
      if (!active) return;
      // Get latest message timestamp as cursor
      var since = '';
      setMessages(function(prev) {
        if (prev.length > 0) {
          var last = prev[prev.length - 1];
          since = last.timestamp || '';
        }
        return prev;
      });
      var url = `/api/linktime/room/${roomAlias}/${meetingCode}/messages?limit=20`;
      if (since) url += '&since=' + encodeURIComponent(since);
      authFetch(url, { skipAuthRedirect: true })
        .then(function(r) { return r.json(); })
        .then(function(data) {
          if (!active) return;
          if (data.messages && data.messages.length) {
            setMessages(function(prev) {
              var existingIds = {};
              prev.forEach(function(m) { if (m._id) existingIds[m._id] = true; });
              var fresh = data.messages.filter(function(m) { return !m._id || !existingIds[m._id]; });
              if (fresh.length === 0) return prev;
              return prev.concat(fresh);
            });
          }
          pollTimer = setTimeout(doPoll, pollMs);
        })
        .catch(function() {
          if (active) pollTimer = setTimeout(doPoll, pollMs);
        });
    };

    doPoll();

    return function() {
      active = false;
      clearTimeout(pollTimer);
    };
  }, [roomAlias, meetingCode]);

  // WebSocket for real-time messages
  var handleWsMessage = useCallback(function(event) {
    try {
      var msg = JSON.parse(event.data);
      if (msg.type === 'linktime_message' && msg.message) {
        setMessages(prev => {
          var exists = prev.some(m =>
            m._id && msg.message._id && m._id === msg.message._id);
          if (exists) return prev;
          return [...prev, msg.message];
        });
        if (collapsedRef.current) {
          setUnread(u => u + 1);
        }
      }
    } catch (_) {}
  }, []);

  var wsSub = useMemo(function() {
    return { roomKey: roomKey, display_name: selfName };
  }, [roomKey, selfName]);
  var wsRef = useSessionPresenceWebSocket(!!(roomAlias && meetingCode), 'LinkTimeChat', handleWsMessage, wsSub);

  // Unsubscribe on unmount (leave is handled by server heartbeat timeout watcher)
  useEffect(() => {
    return function() {
      var ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: 'unsubscribe_room', roomKey }));
        } catch (_) {}
      }
    };
  }, [roomKey]);

  // Auto-scroll
  useEffect(() => {
    if (!collapsed && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, collapsed]);

  // Desktop notification for a fresh incoming message/arrival when the tab is backgrounded.
  // Works while the tab is OPEN (background is fine) — no service worker needed.
  // Pairs with the host SMS alert (server) which covers being away from the computer entirely.
  const lastNotifiedIdRef = useRef(null);
  useEffect(() => {
    if (!messages.length) return;
    var last = messages[messages.length - 1];
    var id = last._id || ((last.timestamp || '') + ':' + (last.senderName || ''));
    if (lastNotifiedIdRef.current === id) return;
    lastNotifiedIdRef.current = id;                                   // fire at most once per message
    if (last.senderName === selfName) return;                        // never notify about my own message
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return;  // only when the tab isn't focused
    if (last.timestamp && (Date.now() - new Date(last.timestamp).getTime()) > 60000) return; // skip history on load
    var body = last.system
      ? (last.senderName + ' ' + systemLabel(last.systemKind))
      : (last.senderName + ': ' + (last.text || ''));
    try {
      var n = new Notification('NumFree · LinkTime', { body: body, tag: 'lt-' + roomAlias + '-' + meetingCode });
      n.onclick = function() { try { window.focus(); } catch (e) {} n.close(); };
    } catch (e) {}
  }, [messages, selfName, roomAlias, meetingCode]);

  function enableNotifications(e) {
    if (e && e.stopPropagation) e.stopPropagation();
    if (typeof Notification === 'undefined') return;
    Notification.requestPermission().then(function(p) { setNotifPerm(p); }).catch(function() {});
  }

  var handleSend = async function(e) {
    e && e.preventDefault();
    var text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput('');
    try {
      var res = await authFetch('/api/linktime/room/' + roomAlias + '/' + meetingCode + '/message', {
        method: 'POST',
        skipAuthRedirect: true,
        headers: { 'Content-Type': 'application/json', 'X-User-Tz': selfTz },
        body: JSON.stringify({ text: text, display_name: selfName })
      });
      if (!res.ok) throw new Error('send failed');
      var saved = await res.json();
      if (saved && saved.text) {
        if (!saved.timestamp) saved.timestamp = new Date().toISOString();
        setMessages(function(prev) {
          var exists = prev.some(function(m) { return m._id && saved._id && m._id === saved._id; });
          if (exists) return prev;
          return prev.concat([saved]);
        });
      }
    } catch (_) {}
    setSending(false);
  };

  var handleKeyDown = function(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  var toggleCollapse = function() {
    setCollapsed(function(c) {
      if (c) setUnread(0);
      return !c;
    });
  };

  if (!roomAlias || !meetingCode) return null;

  return (
    <div className={'lt-chat lt-chat--' + layout + (collapsed ? ' lt-chat--collapsed' : '')}>
      <div
        className="lt-chat__header"
        onClick={toggleCollapse}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
      >
        {layout !== 'rail' && <div className="lt-chat__drag-handle" />}
        <span className="lt-chat__title">Chat</span>
        {unread > 0 && <span className="lt-chat__badge">{unread}</span>}
        {notifPerm !== 'unsupported' && (
          <button
            className="lt-chat__notif-btn"
            type="button"
            title={notifPerm === 'granted'
              ? "Alerts on — you'll be notified when someone arrives or messages, even if this tab is in the background"
              : notifPerm === 'denied'
                ? 'Notifications are blocked — turn them on in your browser settings'
                : 'Turn on alerts for arrivals and messages'}
            onClick={enableNotifications}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, marginLeft: 4, opacity: notifPerm === 'granted' ? 1 : 0.55 }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill={notifPerm === 'granted' ? '#e65100' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 01-3.46 0" />
            </svg>
          </button>
        )}
        <button className="lt-chat__toggle-btn" type="button" aria-label={collapsed ? 'Expand chat' : 'Collapse chat'}>
          {collapsed ? '\u25B2' : '\u25BC'}
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="lt-chat__messages">
            {loading && <div className="lt-chat__loading">Loading...</div>}
            {messages.map(function(msg, i) {
              var hasOtherTz = selfTz && msg.senderTz && msg.senderTz !== selfTz;
              return (
                <div key={msg._id || i} className={'lt-chat__msg' + (msg.system ? ' lt-chat__msg--system' : '') + (msg.senderName === selfName ? ' lt-chat__msg--self' : '')}>
                  {msg.system ? (
                    <div className="lt-chat__system-text">
                      {msg.senderName} {systemLabel(msg.systemKind)}
                      {' \u00B7 '}
                      {fmtTimeTz(msg.timestamp, selfTz)}
                      {hasOtherTz && <span className="lt-chat__tz-alt"> / {fmtTimeTz(msg.timestamp, msg.senderTz)} {abbrTz(msg.senderTz)}</span>}
                    </div>
                  ) : (
                    <>
                      <div className="lt-chat__msg-header">
                        <span className="lt-chat__msg-sender">{msg.senderName}</span>
                        <span className="lt-chat__msg-time">
                          {fmtTimeTz(msg.timestamp, selfTz)}
                          {hasOtherTz && <span className="lt-chat__tz-alt"> / {fmtTimeTz(msg.timestamp, msg.senderTz)} {abbrTz(msg.senderTz)}</span>}
                        </span>
                      </div>
                      <div className="lt-chat__msg-text">{msg.text}</div>
                    </>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <form className="lt-chat__composer" onSubmit={handleSend}>
            <textarea
              className="lt-chat__input"
              value={input}
              onChange={function(e) { setInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              disabled={sending}
            />
            <button className="lt-chat__send-btn" type="submit" disabled={sending || !input.trim()}>
              {sending ? '...' : 'Send'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
